<template>
  <div class="bili-rewrite-page studio-layout">
    <!-- 顶部说明卡片 -->
    <el-card shadow="never" class="intro-card">
      <div class="intro-row">
        <div class="intro-text">
          <p>
            自动获取 B 站视频 → 提取音轨 / 抓字幕 → ASR 转文字 → LLM 洗稿成图文文章。
            支持「单视频链接」「UP 主主页链接」两种输入；任务进度去
            <router-link to="/content/tasks">任务队列</router-link>
            看，最终稿件去
            <router-link to="/content/library">文章管理</router-link>
            看（平台标签：B站）。
          </p>
          <div class="asr-status">
            <el-tag size="small" :type="asrReady ? 'success' : 'danger'">
              ASR：{{ asrProviderLabel }}
            </el-tag>
            <el-tag size="small" type="info" v-if="asrInfo.model">model: {{ asrInfo.model }}</el-tag>
            <el-tag size="small" type="info" v-if="asrInfo.auth_mode">
              鉴权：{{ asrInfo.auth_mode === 'new' ? 'API Key（新版控制台）' : 'AppKey + AccessKey（旧版控制台）' }}
            </el-tag>
            <el-tag size="small" :type="asrReady ? 'success' : 'warning'">
              {{ asrReady ? '已配置鉴权' : '未配置鉴权（请改 config/secrets.json 的 bilibili_rewrite.asr）' }}
            </el-tag>
          </div>
        </div>
      </div>
    </el-card>

    <el-tabs v-model="activeTab" class="bili-tabs">
      <!-- 单作品链接 -->
      <el-tab-pane label="作品链接" name="video">
        <el-card shadow="never" class="form-card">
          <div class="form-section">
            <div class="form-label">B 站作品链接 / BV 号</div>
            <el-input
              v-model="form.video.url"
              type="textarea"
              :rows="3"
              placeholder="例如：https://www.bilibili.com/video/BVxxxxxxxxxx 或 BVxxxxxxxxxx；多个用换行分隔可同时提交"
            />
            <div class="hint">
              <el-icon><InfoFilled /></el-icon>
              字幕优先：若视频有 CC 字幕将直接使用；否则下载音轨走 ASR 兜底。
            </div>
          </div>

          <div class="form-section" v-if="parsedHint.video">
            <el-alert :type="parsedHint.video.kind === 'video' ? 'success' : 'warning'" :closable="false">
              {{ parsedHint.video.text }}
            </el-alert>
          </div>

          <div class="form-section">
            <div class="form-label">附加风格要求（可选）</div>
            <el-input
              v-model="form.video.styleHint"
              type="textarea"
              :rows="2"
              placeholder="例如：偏知乎口吻，3 个二级小标题，文末加一段总结"
            />
          </div>

          <div class="form-actions">
            <el-button type="primary" :loading="submitting" @click="submitVideo">
              <el-icon><VideoPlay /></el-icon>提交洗稿任务
            </el-button>
            <el-button plain @click="resetVideo">清空</el-button>
          </div>
        </el-card>
      </el-tab-pane>

      <!-- UP 主主页链接 -->
      <el-tab-pane label="UP 主主页" name="space">
        <el-card shadow="never" class="form-card">
          <!-- 匿名 Cookie 状态条 -->
          <div class="cookie-status-bar" :class="{ ok: spaceCookieStatus.has_cookie }">
            <div class="cookie-status-text">
              <el-icon v-if="spaceCookieStatus.has_cookie"><Select /></el-icon>
              <el-icon v-else><Warning /></el-icon>
              <span v-if="spaceCookieStatus.has_cookie">
                已配置匿名 Cookie（{{ spaceCookieStatus.keys?.join(', ') || '...' }}），抓取稳定可用
              </span>
              <span v-else>
                未配置匿名 Cookie。IDC 机房 IP 几乎一定会被 B 站风控，<b>强烈建议先配置</b>（一次性、非登录态）。
              </span>
            </div>
            <el-button size="small" :type="spaceCookieStatus.has_cookie ? 'default' : 'primary'" @click="openCookieDialog">
              {{ spaceCookieStatus.has_cookie ? '更新 Cookie' : '配置 Cookie' }}
            </el-button>
          </div>

          <!-- 步骤 1：输入 + 预览 -->
          <div class="form-section">
            <div class="form-label">UP 主主页链接 / mid</div>
            <div class="space-url-row">
              <el-input
                v-model="form.space.url"
                placeholder="例如：https://space.bilibili.com/12345678 或 12345678"
                class="space-url-input"
                @keyup.enter="loadSpaceVideos"
              />
              <el-button type="primary" :loading="loadingSpaceVideos" @click="loadSpaceVideos">
                <el-icon><Search /></el-icon>拉取视频列表
              </el-button>
            </div>
            <div class="hint">
              <el-icon><InfoFilled /></el-icon>
              先抓取该 UP 主最近投稿供你预览勾选；勾选后再提交，只洗稿你选中的那几条。
              <span v-if="!spaceCookieStatus.has_cookie" class="muted-warn">
                · 未配 Cookie 时单次拉取最长可能 30–60 秒，且 ~30% 概率失败
              </span>
            </div>
          </div>

          <div class="form-section" v-if="parsedHint.space">
            <el-alert :type="parsedHint.space.kind === 'space' ? 'success' : 'warning'" :closable="false">
              {{ parsedHint.space.text }}
            </el-alert>
          </div>

          <div class="form-section">
            <div class="form-label">最多预览几条</div>
            <el-input-number v-model="form.space.maxVideos" :min="1" :max="60" />
            <span class="hint-inline">推荐 ≤ 30（一页搞定，最稳）；超过 30 会按 30 翻页，风控触发概率上升</span>
          </div>

          <!-- 步骤 2：列表预览 + 勾选 -->
          <div class="form-section space-preview-section" v-if="spaceVideosLoaded || loadingSpaceVideos">
            <div class="preview-header">
              <div class="preview-title">
                <span>预览：共 <b>{{ spaceVideos.length }}</b> 条</span>
                <span class="muted">·已选 <b>{{ selectedSpaceVideos.length }}</b> 条</span>
                <span class="muted" v-if="spaceTotal > spaceVideos.length">
                  ·该 UP 主总投稿 {{ spaceTotal }} 条
                </span>
              </div>
              <div class="preview-tools">
                <el-input
                  v-model="spaceFilterText"
                  size="small"
                  placeholder="按标题筛选"
                  clearable
                  class="filter-input"
                >
                  <template #prefix><el-icon><Search /></el-icon></template>
                </el-input>
                <el-button size="small" @click="selectAllVisible">全选可见</el-button>
                <el-button size="small" @click="clearSelection">取消勾选</el-button>
              </div>
            </div>

            <el-table
              ref="spaceTableRef"
              :data="filteredSpaceVideos"
              v-loading="loadingSpaceVideos"
              empty-text="未拉到投稿"
              size="small"
              row-key="bvid"
              max-height="420"
              @selection-change="onSpaceSelectionChange"
            >
              <el-table-column type="selection" width="44" :reserve-selection="true" />
              <el-table-column label="封面" width="120">
                <template #default="{ row }">
                  <el-image
                    v-if="row.thumbnail"
                    :src="resolveThumb(row.thumbnail)"
                    fit="cover"
                    style="width: 100px; height: 60px; border-radius: 4px;"
                    referrerpolicy="no-referrer"
                    lazy
                  />
                  <span v-else class="muted">—</span>
                </template>
              </el-table-column>
              <el-table-column label="标题" min-width="280" show-overflow-tooltip>
                <template #default="{ row }">
                  <a :href="row.webpage_url" target="_blank" rel="noreferrer" class="link-cell">
                    {{ row.title || row.bvid }}
                  </a>
                </template>
              </el-table-column>
              <el-table-column label="时长" width="90">
                <template #default="{ row }">
                  {{ row.length_label || formatDuration(row.duration) }}
                </template>
              </el-table-column>
              <el-table-column label="播放" width="100">
                <template #default="{ row }">
                  {{ formatPlayCount(row.view_count) }}
                </template>
              </el-table-column>
              <el-table-column label="发布日期" width="110">
                <template #default="{ row }">
                  {{ formatUploadDate(row.upload_date) }}
                </template>
              </el-table-column>
              <el-table-column prop="bvid" label="BV 号" width="140" />
            </el-table>
          </div>

          <div class="form-section">
            <div class="form-label">附加风格要求（可选）</div>
            <el-input
              v-model="form.space.styleHint"
              type="textarea"
              :rows="2"
              placeholder="例如：偏小红书口吻，多用 emoji，分点表达"
            />
          </div>

          <div class="form-actions">
            <el-button
              type="primary"
              :loading="submitting"
              :disabled="selectedSpaceVideos.length === 0"
              @click="submitSpace"
            >
              <el-icon><VideoPlay /></el-icon>
              提交所选 {{ selectedSpaceVideos.length || 0 }} 条洗稿任务
            </el-button>
            <el-button plain @click="resetSpace">清空</el-button>
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- 最近任务 -->
    <el-card shadow="never" class="recent-card">
      <template #header>
        <div class="recent-header">
          <span>最近的 B站洗稿任务</span>
          <el-button text @click="fetchRecent" :loading="loadingRecent">
            <el-icon><Refresh /></el-icon>刷新
          </el-button>
        </div>
      </template>

      <el-table :data="recentTasks" v-loading="loadingRecent" empty-text="暂无任务" size="small">
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="name" label="任务名称" min-width="220" show-overflow-tooltip />
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="statusType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="进度" width="160">
          <template #default="{ row }">
            <span>{{ row.completed_count || 0 }} / {{ row.total_count || 0 }}</span>
            <span v-if="row.failed_count > 0" class="failed-count">（失败 {{ row.failed_count }}）</span>
          </template>
        </el-table-column>
        <el-table-column prop="current_stage" label="当前阶段" width="180" show-overflow-tooltip />
        <el-table-column label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatTime(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160">
          <template #default="{ row }">
            <el-button size="small" text @click="goTaskDetail(row.id)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 匿名 Cookie 配置 Dialog -->
    <el-dialog v-model="cookieDialogVisible" title="配置匿名 Cookie（不是登录态）" width="640px">
      <el-alert type="info" :closable="false" show-icon class="cookie-alert">
        <template #title>这不是登录态！只是每个浏览器访问 B 站都会拿到的"匿名访客 ID"。</template>
        <div class="cookie-help">
          <b>获取步骤：</b>
          <ol>
            <li>用 <b>未登录的浏览器</b>（或者退出登录 / 隐身窗口）打开 <a href="https://www.bilibili.com" target="_blank">https://www.bilibili.com</a></li>
            <li>F12 打开开发者工具 → <b>Application</b>（或 Storage）→ <b>Cookies</b> → 选 <code>https://www.bilibili.com</code></li>
            <li>把 <code>buvid3</code>、<code>buvid4</code>、<code>b_nut</code>、<code>_uuid</code>、<code>b_lsid</code> 这几条复制下来</li>
            <li>拼成 <code>buvid3=xxx; buvid4=xxx; b_nut=xxx; _uuid=xxx; b_lsid=xxx</code> 粘贴到下方</li>
          </ol>
          <div class="cookie-help-tip">最少给 <code>buvid3</code> 一条也能用，但全部填齐风控通过率更高。</div>
        </div>
      </el-alert>
      <el-input
        v-model="cookieDraft"
        type="textarea"
        :rows="4"
        placeholder="buvid3=xxxx; buvid4=xxxx; b_nut=xxxx; _uuid=xxxx; b_lsid=xxxx"
        class="cookie-input"
      />
      <template #footer>
        <el-button @click="cookieDialogVisible = false">取消</el-button>
        <el-button v-if="spaceCookieStatus.has_cookie" type="danger" plain @click="clearCookie" :loading="savingCookie">
          清空
        </el-button>
        <el-button type="primary" @click="saveCookie" :loading="savingCookie">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { InfoFilled, VideoPlay, Refresh, Search, Select, Warning } from '@element-plus/icons-vue'
import { bilibiliRewriteApi } from '../api'

export default {
  name: 'BilibiliRewrite',
  components: { InfoFilled, VideoPlay, Refresh, Search, Select, Warning },
  setup() {
    const router = useRouter()
    const activeTab = ref('video')
    const submitting = ref(false)
    const loadingRecent = ref(false)
    const recentTasks = ref([])
    const asrInfo = ref({})
    let pollTimer = null

    const form = reactive({
      video: { url: '', styleHint: '' },
      space: { url: '', styleHint: '', maxVideos: 30 },
    })

    const parsedHint = reactive({ video: null, space: null })

    // UP 主主页预览相关状态
    const loadingSpaceVideos = ref(false)
    const spaceVideosLoaded = ref(false)
    const spaceVideos = ref([])
    const spaceTotal = ref(0)
    const selectedSpaceVideos = ref([])
    const spaceFilterText = ref('')
    const spaceTableRef = ref(null)
    const spaceCookieStatus = ref({ has_cookie: false, keys: [], has_buvid3: false })
    const cookieDialogVisible = ref(false)
    const cookieDraft = ref('')
    const savingCookie = ref(false)

    const asrReady = computed(() => !!asrInfo.value.has_key)

    const ASR_PROVIDER_LABEL = {
      volc_auc_turbo: '火山豆包 · 极速版',
    }
    const asrProviderLabel = computed(() => {
      const p = asrInfo.value.provider
      if (!p) return '—'
      return ASR_PROVIDER_LABEL[p] || p
    })

    const statusType = (s) => ({
      pending: 'info',
      running: 'warning',
      completed: 'success',
      failed: 'danger',
    }[s] || 'info')

    const statusLabel = (s) => ({
      pending: '排队中',
      running: '运行中',
      completed: '已完成',
      failed: '失败',
    }[s] || s)

    const formatTime = (iso) => {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }

    const formatDuration = (sec) => {
      const s = Number(sec) || 0
      if (s <= 0) return '—'
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const ss = s % 60
      const pad = (n) => String(n).padStart(2, '0')
      return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`
    }

    const formatPlayCount = (n) => {
      const v = Number(n) || 0
      if (v >= 10000) return `${(v / 10000).toFixed(1)}万`
      return String(v)
    }

    const formatUploadDate = (yyyymmdd) => {
      const s = String(yyyymmdd || '')
      if (s.length !== 8) return s || '—'
      return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
    }

    // B 站封面图 referrer 检查严，走 images.weserv.nl 兜底（生产环境可后端代理）
    const resolveThumb = (url) => {
      if (!url) return ''
      try {
        const u = new URL(url, 'https://www.bilibili.com')
        const noScheme = u.host + u.pathname + u.search
        return `https://images.weserv.nl/?url=${encodeURIComponent(noScheme)}`
      } catch (e) {
        return url
      }
    }

    const fetchAsrStatus = async () => {
      try {
        asrInfo.value = await bilibiliRewriteApi.asrStatus()
      } catch (e) {
        asrInfo.value = {}
      }
    }

    const fetchCookieStatus = async () => {
      try {
        spaceCookieStatus.value = await bilibiliRewriteApi.getSpaceCookie()
      } catch (e) {
        spaceCookieStatus.value = { has_cookie: false, keys: [], has_buvid3: false }
      }
    }

    const openCookieDialog = () => {
      cookieDraft.value = ''
      cookieDialogVisible.value = true
    }

    const saveCookie = async () => {
      const c = (cookieDraft.value || '').trim()
      if (!c) {
        ElMessage.warning('请粘贴 cookie 字符串')
        return
      }
      savingCookie.value = true
      try {
        await bilibiliRewriteApi.saveSpaceCookie({ cookie: c })
        ElMessage.success('已保存匿名 Cookie，现在可以稳定拉取了')
        cookieDialogVisible.value = false
        await fetchCookieStatus()
      } catch (e) {
        ElMessage.error(e.message || '保存失败')
      } finally {
        savingCookie.value = false
      }
    }

    const clearCookie = async () => {
      savingCookie.value = true
      try {
        await bilibiliRewriteApi.saveSpaceCookie({ cookie: '' })
        ElMessage.success('已清空匿名 Cookie，将回退到纯匿名模式（可能被风控）')
        cookieDialogVisible.value = false
        await fetchCookieStatus()
      } catch (e) {
        ElMessage.error(e.message || '清空失败')
      } finally {
        savingCookie.value = false
      }
    }

    const fetchRecent = async () => {
      loadingRecent.value = true
      try {
        const res = await bilibiliRewriteApi.tasks({ limit: 20 })
        recentTasks.value = res.items || []
      } catch (e) {
        ElMessage.error(e.message || '获取任务列表失败')
      } finally {
        loadingRecent.value = false
      }
    }

    const tryParse = async (raw, slot) => {
      const s = (raw || '').trim()
      if (!s) {
        parsedHint[slot] = null
        return
      }
      try {
        const info = await bilibiliRewriteApi.parse({ raw: s })
        if (info.kind === 'video') {
          parsedHint[slot] = { kind: 'video', text: `识别为 B 站作品：${info.id || info.url}` }
        } else if (info.kind === 'space') {
          parsedHint[slot] = { kind: 'space', text: `识别为 UP 主主页：mid=${info.id}` }
        } else {
          parsedHint[slot] = { kind: 'unknown', text: '无法识别为 B 站链接' }
        }
      } catch (e) {
        parsedHint[slot] = { kind: 'unknown', text: e.message || '无法识别' }
      }
    }

    let parseTimerVideo = null
    let parseTimerSpace = null
    watch(() => form.video.url, (v) => {
      if (parseTimerVideo) clearTimeout(parseTimerVideo)
      const firstLine = (v || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean)[0] || ''
      parseTimerVideo = setTimeout(() => tryParse(firstLine, 'video'), 400)
    })
    watch(() => form.space.url, (v) => {
      if (parseTimerSpace) clearTimeout(parseTimerSpace)
      parseTimerSpace = setTimeout(() => tryParse(v, 'space'), 400)
    })

    const submitVideo = async () => {
      const lines = (form.video.url || '')
        .split(/\r?\n/).map(s => s.trim()).filter(Boolean)
      if (!lines.length) {
        ElMessage.warning('请先填写 B 站作品链接 / BV 号')
        return
      }
      if (!asrReady.value) {
        ElMessage.warning('当前 ASR（火山豆包 · 极速版）未配置鉴权，遇到无字幕视频会失败。请先在 config/secrets.json 的 bilibili_rewrite.asr 填入 api_key 或 app_key+access_key')
      }
      submitting.value = true
      try {
        const res = await bilibiliRewriteApi.submit({
          inputs: lines,
          max_videos_per_space: 1,
          style_hint: form.video.styleHint || '',
          target_platform: 'bilibili',
        })
        ElMessage.success(`已创建任务 #${res.task_id}（${res.inputs_count} 项）`)
        await fetchRecent()
      } catch (e) {
        ElMessage.error(e.message || '提交失败')
      } finally {
        submitting.value = false
      }
    }

    // UP 主主页：拉取投稿列表供预览
    const filteredSpaceVideos = computed(() => {
      const q = (spaceFilterText.value || '').trim().toLowerCase()
      if (!q) return spaceVideos.value
      return spaceVideos.value.filter(v =>
        (v.title || '').toLowerCase().includes(q)
        || (v.bvid || '').toLowerCase().includes(q)
      )
    })

    const onSpaceSelectionChange = (rows) => {
      selectedSpaceVideos.value = rows || []
    }

    const selectAllVisible = () => {
      const tbl = spaceTableRef.value
      if (!tbl) return
      const visible = filteredSpaceVideos.value
      visible.forEach(row => tbl.toggleRowSelection(row, true))
    }

    const clearSelection = () => {
      const tbl = spaceTableRef.value
      if (tbl) tbl.clearSelection()
      selectedSpaceVideos.value = []
    }

    const loadSpaceVideos = async () => {
      const url = (form.space.url || '').trim()
      if (!url) {
        ElMessage.warning('请先填写 UP 主主页链接 / mid')
        return
      }
      loadingSpaceVideos.value = true
      try {
        const res = await bilibiliRewriteApi.spaceVideos({
          url,
          max_count: Math.max(1, Math.min(50, Number(form.space.maxVideos) || 20)),
        })
        spaceVideos.value = res.items || []
        spaceTotal.value = res.count >= (res.items?.length || 0)
          ? (res.items?.length || 0)
          : 0
        // total 字段后端用的是返回数量，这里用 count；UP 主总数后端没透出，先用 count 兜底
        spaceVideosLoaded.value = true
        if (spaceTableRef.value) spaceTableRef.value.clearSelection()
        selectedSpaceVideos.value = []
        if (!spaceVideos.value.length) {
          ElMessage.warning('该 UP 主当前没有可拉取的投稿')
        } else {
          ElMessage.success(`已拉到 ${spaceVideos.value.length} 条投稿，请勾选要洗稿的视频`)
        }
      } catch (e) {
        ElMessage.error(e.message || '拉取 UP 主投稿失败')
      } finally {
        loadingSpaceVideos.value = false
      }
    }

    const submitSpace = async () => {
      if (!selectedSpaceVideos.value.length) {
        ElMessage.warning('请先勾选要洗稿的视频')
        return
      }
      if (!asrReady.value) {
        ElMessage.warning('当前 ASR（火山豆包 · 极速版）未配置鉴权，遇到无字幕视频会失败。请先在 config/secrets.json 的 bilibili_rewrite.asr 填入 api_key 或 app_key+access_key')
      }
      const inputs = selectedSpaceVideos.value
        .map(v => v.webpage_url || (v.bvid ? `https://www.bilibili.com/video/${v.bvid}` : ''))
        .filter(Boolean)
      if (!inputs.length) {
        ElMessage.warning('选中的条目没有可用链接')
        return
      }
      submitting.value = true
      try {
        const res = await bilibiliRewriteApi.submit({
          inputs,
          max_videos_per_space: 1,  // 每个 input 都是单视频，不再展开
          style_hint: form.space.styleHint || '',
          target_platform: 'bilibili',
        })
        ElMessage.success(`已创建任务 #${res.task_id}，将处理 ${inputs.length} 条选中的视频`)
        clearSelection()
        await fetchRecent()
      } catch (e) {
        ElMessage.error(e.message || '提交失败')
      } finally {
        submitting.value = false
      }
    }

    const resetVideo = () => { form.video.url = ''; form.video.styleHint = ''; parsedHint.video = null }
    const resetSpace = () => {
      form.space.url = ''
      form.space.styleHint = ''
      form.space.maxVideos = 30
      parsedHint.space = null
      spaceVideos.value = []
      spaceVideosLoaded.value = false
      spaceTotal.value = 0
      spaceFilterText.value = ''
      selectedSpaceVideos.value = []
    }

    const goTaskDetail = (id) => {
      router.push({ path: '/content/tasks', query: { focus: id } })
    }

    onMounted(() => {
      fetchAsrStatus()
      fetchRecent()
      fetchCookieStatus()
      pollTimer = setInterval(() => {
        const hasRunning = recentTasks.value.some(t => t.status === 'running' || t.status === 'pending')
        if (hasRunning) fetchRecent()
      }, 5000)
    })
    onUnmounted(() => {
      if (pollTimer) clearInterval(pollTimer)
    })

    return {
      activeTab, submitting, loadingRecent, recentTasks, asrInfo, asrReady, asrProviderLabel,
      form, parsedHint,
      submitVideo, submitSpace, resetVideo, resetSpace,
      fetchRecent, goTaskDetail,
      statusType, statusLabel, formatTime,
      loadingSpaceVideos, spaceVideosLoaded, spaceVideos, spaceTotal, selectedSpaceVideos,
      spaceFilterText, spaceTableRef,
      filteredSpaceVideos, onSpaceSelectionChange, selectAllVisible, clearSelection,
      loadSpaceVideos,
      formatDuration, formatPlayCount, formatUploadDate, resolveThumb,
      spaceCookieStatus, cookieDialogVisible, cookieDraft, savingCookie,
      openCookieDialog, saveCookie, clearCookie,
    }
  },
}
</script>

<style scoped>
.bili-rewrite-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.intro-card :deep(.el-card__body) {
  padding: 16px 20px;
}

.intro-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}



.intro-text p {
  margin: 0 0 10px 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.intro-text a {
  color: #409EFF;
  text-decoration: none;
  margin: 0 2px;
}

.intro-text a:hover {
  text-decoration: underline;
}

.asr-status {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.bili-tabs :deep(.el-tabs__nav-wrap)::after {
  height: 1px;
}

.form-card :deep(.el-card__body) {
  padding: 20px;
}

.form-section {
  margin-bottom: 18px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 8px;
}

.hint {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

.recent-card :deep(.el-card__header) {
  padding: 12px 20px;
}

.recent-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.failed-count {
  color: #f56c6c;
  margin-left: 4px;
  font-size: 12px;
}

/* UP 主主页：拉取 + 预览 */
.space-url-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.space-url-input {
  flex: 1;
}
.hint-inline {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
.space-preview-section {
  background: #fafafa;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #ebeef5;
}
.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
  flex-wrap: wrap;
}
.preview-title {
  font-size: 13px;
  color: #303133;
}
.preview-title b {
  color: #409EFF;
  margin: 0 2px;
}
.preview-title .muted {
  color: #909399;
  margin-left: 10px;
}
.preview-tools {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.filter-input {
  width: 180px;
}
.link-cell {
  color: #303133;
  text-decoration: none;
}
.link-cell:hover {
  color: #409EFF;
  text-decoration: underline;
}
.muted {
  color: #909399;
}
.muted-warn {
  color: #e6a23c;
  margin-left: 6px;
}
.cookie-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff7e6;
  border: 1px solid #faecd8;
  color: #e6a23c;
  font-size: 13px;
  margin-bottom: 16px;
}
.cookie-status-bar.ok {
  background: #f0f9eb;
  border-color: #e1f3d8;
  color: #67c23a;
}
.cookie-status-text {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}
.cookie-status-text b {
  color: inherit;
}
.cookie-alert {
  margin-bottom: 12px;
}
.cookie-help ol {
  margin: 4px 0 0 18px;
  padding: 0;
  font-size: 12px;
  line-height: 1.8;
  color: #303133;
}
.cookie-help code {
  background: #f5f5f5;
  padding: 1px 5px;
  border-radius: 3px;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
}
.cookie-help-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.cookie-input {
  font-family: ui-monospace, Menlo, Consolas, monospace;
}
</style>
